// Frontend API wrapper for the team-membership admin surface (PRODOPSAI-508).
// Endpoint paths are passed WITHOUT the `/api` prefix — the `api` wrapper adds
// it (CLAUDE.md). PR 3 wires up `getTeamMembershipTree` only; the create /
// update / delete / role-catalog stubs exist so US7 + US8 can plug in without
// changing this file's public surface.

import { api } from '@/lib/api';
import type {
  IApiResponse,
  ICreateTeamMembershipRequest,
  ICreateTeamRoleRequest,
  ITeamMembership,
  ITeamMembershipTreeResponse,
  ITeamRole,
  IUpdateTeamMembershipRequest,
} from '@skystone/shared';

interface GetTreeOptions {
  includeArchivedTeams?: boolean;
}

function buildTreeUrl(options: GetTreeOptions): string {
  if (options.includeArchivedTeams === true) {
    return '/admin/team-memberships/tree?includeArchivedTeams=true';
  }
  return '/admin/team-memberships/tree';
}

export const teamMembershipApi = {
  /** Fetch the entire Team -> Role -> Person tree + unmapped list in one round-trip. */
  getTeamMembershipTree: async (options: GetTreeOptions = {}): Promise<ITeamMembershipTreeResponse> => {
    const result = await api.get<IApiResponse<ITeamMembershipTreeResponse>>(buildTreeUrl(options));
    return result.data;
  },

  /** Role catalog (US7 wires this in). */
  getTeamRoles: async (): Promise<ITeamRole[]> => {
    const result = await api.get<IApiResponse<ITeamRole[]>>('/admin/team-roles');
    return result.data;
  },

  /** Create a new team role (PRODOPSAI-521 inline mini-form). */
  createTeamRole: async (body: ICreateTeamRoleRequest): Promise<ITeamRole> => {
    const result = await api.post<IApiResponse<ITeamRole>>('/admin/team-roles', body);
    return result.data;
  },

  /** Create a membership (US7 uses this). */
  createTeamMembership: async (
    body: ICreateTeamMembershipRequest,
  ): Promise<ITeamMembership> => {
    const result = await api.post<IApiResponse<ITeamMembership>>('/admin/team-memberships', body);
    return result.data;
  },

  /** Patch a membership (US8 uses this). */
  updateTeamMembership: async (
    id: string,
    body: IUpdateTeamMembershipRequest,
  ): Promise<ITeamMembership> => {
    const result = await api.patch<IApiResponse<ITeamMembership>>(`/admin/team-memberships/${id}`, body);
    return result.data;
  },

  /** Delete a membership (US8 uses this). */
  deleteTeamMembership: async (id: string): Promise<void> => {
    await api.delete(`/admin/team-memberships/${id}`);
  },
};
